'use es6';

import { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { DragSource, DropTarget } from 'react-dnd-compat';
import { getEmptyImage, NativeTypes } from 'react-dnd-ipeui-backend';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SnapPlaceholder, SHADE_AUTO, SHADE_BOTH } from 'LayoutBuilderUI/components/dragAndDrop/SnapPlaceholder';
import { getSmartRulesCount } from 'ContentEditorUI/lib/smartContent/utils';
import { makeAsyncComponentWithNoStates } from 'ContentEditorUI/components/shared/makeAsyncUtils';
import { openModal } from 'ContentEditorUI/redux/actions/appActions';
import { getIsImageModule, getIsVideoModule, getPaddingConfig, EMPTY_PADDING_CONFIG } from 'EmailEditorUI/utils/moduleUtils';
import { getElementById, getRenderedOutputById, getModuleSpec, getIsModuleEditable, getIsModuleInlineEditable } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import { getForcedHighlightedElementId, getHighlightedId, getSelectedId } from 'EmailEditorUI/redux/selectors/selected';
import { getIsEmptyImageModule, getIsEmptyVideoModule, getShouldUsePlaceholderForModule } from 'EmailEditorUI/utils/emailPlaceholderUtils';
import { NamespaceWrapper } from 'EmailEditorUI/components/NamespaceWrapper';
import { getModuleLoggingType } from 'EmailEditorUI/utils/usageTracking';
import { DropPosition } from 'EmailEditorUI/redux/actions/types/layoutActions';
import { isUsingDynamicData, isUsingFakePlaceholderData } from 'EmailEditorUI/js/utils/moduleUtils';
import { getIsSingleColumnFullWidth } from 'EmailEditorUI/redux/selectors/styleSettingsSelectors';
import { renderModule } from 'EmailEditorUI/redux/actions/RenderActions';
import { getIsReadOnlyMode } from 'EmailEditorUI/redux/selectors/ReadOnlySelector';
import ScopedModuleMessage from 'EmailEditorUI/containers/sidebar/ScopedModuleMessage';
import GrayScaleWhenHidden from 'LayoutBuilderUI/components/overlays/GrayScaleWhenHidden';
import { addNewColumn, dragElement, dropElementOnElement, dropElementOnPlaceholder, dropElementOnSection, dropNewImageElements, dropNewVideoElements, selectElement, uploadImageForModule, uploadVideoForModule } from 'LayoutBuilderUI/actions/layoutActions';
import { logInteraction } from 'LayoutBuilderUI/actions/ApiActions';
import { SECTION, ELEMENT, ELEMENT_PLACEHOLDER } from 'LayoutBuilderUI/lib/DragDropConstants';
import { CustomWidget } from 'LayoutBuilderUI/components/editors/CustomWidget';
import { getMouseOverElementId, getIsFileManagerOpen } from 'LayoutBuilderUI/selectors/userInterface';
import { getDropTargetPositionForHoverBothDirections, getDropTargetPositionForHover } from 'LayoutBuilderUI/utils/DragDropUtils';
import withDropComponent from 'LayoutBuilderUI/hoc/withDropComponent';
import MouseEventsHandler from 'LayoutBuilderUI/components/utility/MouseEventsHandler';
import SmartModuleLabel from 'LayoutBuilderUI/components/overlays/SmartModuleLabel';
import { getIsInMobileBreakpointMode } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { withErrorBoundaryConnect, ModuleRenderError } from 'EmailEditorUI/hoc/withErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const withElementWrapper = WrappedComponent => props => props.element ? /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props)) : null;
const AsyncElementAffordances = makeAsyncComponentWithNoStates(() => import( /* webpackChunkName: 'element-affordances' */'LayoutBuilderUI/components/overlays/ElementAffordances'));
const AsyncDynamicModuleLabel = makeAsyncComponentWithNoStates(() => import( /* webpackChunkName: 'element-dynamic-data-label' */'LayoutBuilderUI/components/overlays/DynamicModuleLabel'));
const AsyncFakePlaceholderDataAlert = makeAsyncComponentWithNoStates(() => import( /* webpackChunkName: 'element-fake-data-alert' */'LayoutBuilderUI/components/overlays/FakePlaceholderDataAlert'));
const mapStateToProps = (state, ownProps) => {
  const element = getElementById(state, ownProps.elementId);
  if (!element) {
    return {};
  }
  const renderedOutput = getRenderedOutputById(state, ownProps.elementId);
  const id = element.get('id');
  const moduleId = element.get('module_id');
  const isInlineEditable = getIsModuleInlineEditable(state, moduleId);
  const isEditable = getIsModuleEditable(state, moduleId, id);
  const isFilePlaceholder = getIsEmptyImageModule(element) || getIsEmptyVideoModule(element);
  const isRenderedImage = (getIsImageModule(element) || getIsVideoModule(element)) && !isFilePlaceholder;
  const isSelected = getSelectedId(state) === id;
  const moduleSpec = getModuleSpec(state, element);
  const isForciblyHighlighted = getForcedHighlightedElementId(state) === id || getForcedHighlightedElementId(state) === element.get('name');
  const isMobileBreakpointMode = getIsInMobileBreakpointMode(state);
  const isNarrowColumn = !isMobileBreakpointMode && ownProps.columnWidth <= 4;
  return {
    renderedOutput,
    element,
    mouseIsOver: getMouseOverElementId(state) === id,
    somethingSelected: !!getSelectedId(state),
    isInlineEditable,
    isEditable,
    isRenderedImage,
    isFilePlaceholder,
    isNarrowColumn,
    isSelected,
    isSingleColumnFullWidth: getIsSingleColumnFullWidth(state),
    isFileManagerOpen: getIsFileManagerOpen(state),
    numSmartRules: getSmartRulesCount(element),
    moduleSpec,
    isHighlighted: getHighlightedId(state) === id || getHighlightedId(state) === element.get('name') || isForciblyHighlighted,
    shouldShowPlaceholder: getShouldUsePlaceholderForModule({
      module: element,
      checkRenderedOutput: !isInlineEditable,
      renderedOutput,
      moduleSpec,
      state
    }),
    isMobileBreakpointMode,
    isReadOnlyMode: getIsReadOnlyMode(state)
  };
};
const mapDispatchToProps = {
  dragElement,
  dropElementOnSection,
  dropElementOnElement,
  dropElementOnPlaceholder,
  selectElement,
  uploadImageForModule,
  uploadVideoForModule,
  dropNewImageElements,
  dropNewVideoElements,
  retryRenderModule: renderModule,
  addNewColumn,
  openModalAction: openModal
};
const widgetDragSource = {
  beginDrag(props, monitor, component) {
    const {
      element,
      index,
      sectionIndex,
      columnIndex
    } = props;
    props.dragElement();
    return {
      id: element.get('id'),
      moduleId: element.get('module_id'),
      sectionIndex,
      columnIndex,
      index,
      component
    };
  },
  endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }
    const dropResult = monitor.getDropResult();
    const item = monitor.getItem();
    logInteraction({
      action: 'Modify Layout Action',
      event: 'move-module',
      'module-type': getModuleLoggingType(item.moduleId)
    });
    if (dropResult.type === SECTION) {
      if (props.isSingleColumnFullWidth) {
        return;
      }
      props.dropElementOnSection({
        elementId: item.id,
        dropPosition: dropResult.dropPosition,
        targetSectionIndex: dropResult.sectionIndex,
        forceDropOutsideBoxed: dropResult.forceDropOutsideBoxed
      });
    } else if (dropResult.type === ELEMENT) {
      const {
        isVertical,
        dropPosition,
        columnIndex,
        sectionIndex
      } = dropResult;
      if (isVertical) {
        const index = dropPosition === DropPosition.AFTER ? columnIndex + 1 : columnIndex;
        props.addNewColumn(sectionIndex, index, {
          elementId: item.id,
          adjacentColumnIndex: columnIndex,
          dropPosition
        });
      } else {
        props.dropElementOnElement({
          elementId: item.id,
          targetElementId: dropResult.elementId,
          dropPosition: dropResult.dropPosition
        });
      }
    } else if (dropResult.type === ELEMENT_PLACEHOLDER) {
      props.dropElementOnPlaceholder({
        elementId: item.id,
        placeholderSectionId: dropResult.sectionId,
        placeholderColumnId: dropResult.columnId
      });
    } else {
      console.error(`Unexpected element drop on target type ${dropResult.type}`);
    }
  }
};
const widgetDragTarget = {
  drop(props, monitor) {
    const {
      sectionIndex,
      columnIndex,
      index,
      isFilePlaceholder,
      canAddNewColumn,
      element
    } = props;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Determine rectangle on screen
    const targetBoundingRect = props.getRect();
    let dropPosition;
    let isVertical;
    if (canAddNewColumn) {
      const positioning = getDropTargetPositionForHoverBothDirections(targetBoundingRect, clientOffset);
      dropPosition = positioning.position;
      isVertical = positioning.isVertical;
    } else {
      isVertical = false;
      dropPosition = getDropTargetPositionForHover(targetBoundingRect, clientOffset);
    }
    const item = monitor.getItem();
    const dropProps = {
      type: ELEMENT,
      sectionIndex,
      columnIndex,
      elementIndex: index,
      elementId: props.element.get('id'),
      dropPosition,
      isVertical
    };
    if (monitor.getItemType() === NativeTypes.FILE) {
      if (item.files && item.files.length && isFilePlaceholder) {
        let files = item.files;
        if (getIsImageModule(element)) {
          const elementId = props.element.get('id');
          props.uploadImageForModule(elementId, files[0]);
          files = files.slice(1);
          if (files.length > 0) {
            props.dropNewImageElements(Object.assign({
              files
            }, dropProps));
          }
        } else if (getIsVideoModule(element)) {
          const elementId = props.element.get('id');
          props.uploadVideoForModule(elementId, files[0]);
          files = files.slice(1);
          if (files.length > 0) {
            props.dropNewVideoElements(Object.assign({
              files
            }, dropProps));
          }
        }
      }
      return undefined;
    }
    return dropProps;
  },
  hover(props, monitor) {
    if (monitor.getItemType() === NativeTypes.FILE && props.isImagePlaceholder) {
      return;
    }
    // Determine mouse position
    const clientOffset = monitor.getClientOffset();
    // Determine rectangle on screen
    const targetBoundingRect = props.getRect();
    let dropPosition;
    let isVertical;
    if (props.canAddNewColumn) {
      const positioning = getDropTargetPositionForHoverBothDirections(targetBoundingRect, clientOffset);
      dropPosition = positioning.position;
      isVertical = positioning.isVertical;
    } else {
      isVertical = false;
      dropPosition = getDropTargetPositionForHover(targetBoundingRect, clientOffset);
    }
    props.updateSnapPlaceholderPosition(dropPosition, isVertical);
  },
  canDrop: props => !props.isFileManagerOpen
};
class Element extends Component {
  constructor(...args) {
    super(...args);
    this.handleClick = () => {
      const {
        isSelected,
        element,
        isEditable
      } = this.props;
      if (!isSelected && isEditable) {
        this.props.selectElement(element.get('id'));
      }
    };
    this.maybeRenderSmartModuleLabel = () => /*#__PURE__*/_jsx(SmartModuleLabel, {
      element: this.props.element,
      isSelected: this.props.isSelected,
      isNarrowColumn: this.props.isNarrowColumn
    }, this.props.element.get('module_id'));
    this.maybeRenderFakePlaceholderDataAlert = () => {
      const {
        element,
        renderedOutput,
        isNarrowColumn
      } = this.props;
      if (isUsingFakePlaceholderData(element, renderedOutput)) {
        return /*#__PURE__*/_jsx(GrayScaleWhenHidden, {
          children: /*#__PURE__*/_jsx(AsyncFakePlaceholderDataAlert, {
            element: element,
            isCompact: isNarrowColumn
          })
        });
      }
      return null;
    };
    this.maybeRenderDynamicDataLabel = () => {
      const {
        element,
        renderedOutput,
        isNarrowColumn,
        isSelected
      } = this.props;
      if (isUsingDynamicData(element, renderedOutput) && !isSelected) {
        return /*#__PURE__*/_jsx(GrayScaleWhenHidden, {
          children: /*#__PURE__*/_jsx(AsyncDynamicModuleLabel, {
            element: element,
            isCompact: isNarrowColumn
          })
        });
      }
      return null;
    };
    this.maybeRenderAlertAndLabels = () => {
      const renderedAlerts = [this.maybeRenderFakePlaceholderDataAlert(), this.maybeRenderDynamicDataLabel(), this.maybeRenderSmartModuleLabel()];
      if (renderedAlerts.filter(alert => alert).length === 0) {
        return null;
      }
      return /*#__PURE__*/_jsx(NamespaceWrapper, {
        children: renderedAlerts
      });
    };
    this.renderScopedModuleInUseMessage = () => {
      const {
        isEditable,
        moduleSpec,
        element
      } = this.props;
      if (moduleSpec && !isEditable) {
        return /*#__PURE__*/_jsx(ScopedModuleMessage, {
          moduleId: element.get('module_id')
        });
      }
      return null;
    };
    this.renderContentWithSnapPlaceholder = content => {
      const {
        isOver,
        snapPlaceholderPosition,
        isVerticalSnapPlaceholder,
        isHoveringFile,
        isFilePlaceholder,
        canResize,
        index,
        columnHeight
      } = this.props;
      if (isHoveringFile && isFilePlaceholder) {
        return content;
      }

      // Shade appropriately when multiple columns exist in section
      let shade;
      if (snapPlaceholderPosition === DropPosition.BEFORE && index === 0 || snapPlaceholderPosition === DropPosition.AFTER && index === columnHeight - 1) {
        shade = SHADE_AUTO;
      } else {
        shade = SHADE_BOTH;
      }
      const element =
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsxs("div", {
        className: "no-margin-collapse",
        style: {
          position: 'relative'
        },
        children: [/*#__PURE__*/_jsx(SnapPlaceholder, {
          isVisible: isOver && !isVerticalSnapPlaceholder,
          position: snapPlaceholderPosition,
          shade: shade,
          isVertical: false
        }), content]
      });
      return /*#__PURE__*/_jsxs("div", {
        "data-test-id": "vertical-placeholder-container",
        children: [canResize && /*#__PURE__*/_jsx(SnapPlaceholder, {
          isVisible: isOver && isVerticalSnapPlaceholder,
          position: snapPlaceholderPosition,
          shade: shade,
          isVertical: true
        }), element]
      });
    };
  }
  componentDidMount() {
    this.props.connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }
  shouldComponentUpdate() {
    return !this.props.isResizing;
  }
  keyCommandHandler() {
    return false;
  }
  getCanUploadFile() {
    const {
      isHoveringFile,
      isFilePlaceholder
    } = this.props;
    return isHoveringFile && isFilePlaceholder;
  }
  getEditor(isInline) {
    const {
      element,
      renderedOutput,
      sectionIndex,
      columnIndex,
      index,
      isSelected,
      mouseIsOver,
      retryRenderModule,
      isRenderedImage,
      isSingleColumnFullWidth,
      isEditable,
      setIsHoveringSubSection,
      isHighlighted,
      stack
    } = this.props;
    const paddingConfig = isRenderedImage ? EMPTY_PADDING_CONFIG : getPaddingConfig(element, isSingleColumnFullWidth);
    const editorProps = {
      id: element.get('id'),
      element,
      renderedOutput,
      sectionIndex,
      columnIndex,
      index,
      isSelected,
      userHover: mouseIsOver || isHighlighted,
      paddingConfig,
      inlineEditable: isInline,
      fileHover: this.getCanUploadFile(),
      retryRenderModule,
      containerRef: this.node,
      isBlocked: !isEditable,
      blockedModuleMessage: this.renderScopedModuleInUseMessage(),
      setIsHoveringSubSection,
      isInFlexArea: true,
      isRenderedImage,
      stack
    };
    return /*#__PURE__*/_jsx(CustomWidget, Object.assign({}, editorProps));
  }
  renderAffordances() {
    const {
      isSelected,
      mouseIsOver,
      isSingleColumnFullWidth,
      isHighlighted,
      isMobileBreakpointMode,
      isReadOnlyMode
    } = this.props;
    if (!(isSelected || isHighlighted || mouseIsOver)) {
      return null;
    }
    return /*#__PURE__*/_jsx(AsyncElementAffordances, {
      "data-test-id": "element-affordances",
      isSelected: isSelected,
      isOver: mouseIsOver,
      shouldUseInside: isSingleColumnFullWidth,
      displayDragHandle: !isReadOnlyMode,
      isHighlighted: isHighlighted,
      message: this.renderScopedModuleInUseMessage(),
      isMobileBreakpointMode: isMobileBreakpointMode
    });
  }
  render() {
    const {
      isOver,
      isSelected,
      isDragging,
      connectDragSource,
      connectDropTarget,
      somethingSelected,
      isInlineEditable,
      element,
      shouldShowPlaceholder,
      isEditable,
      isHighlighted
    } = this.props;
    if (!element) {
      return null;
    }
    const draggable = !somethingSelected;
    const wrapperClasses = classNames("cos-element no-margin-collapse", isInlineEditable && 'inline-editable' + (shouldShowPlaceholder ? " _hs_shouldShowPlaceholder" : ""), isSelected && "selected", isOver && "hovered", this.getCanUploadFile() && 'file-hover', draggable && "draggable", isDragging && "isDragging", isHighlighted && "highlighted", !isEditable && "blocked");
    let content = connectDropTarget(
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx("div", {
      className: wrapperClasses,
      "data-selenium": "content-module",
      "data-onboarding": "content-module",
      id: element.get('id'),
      ref: ref => {
        this.props.getElementRef(ref);
        this.node = ref;
      },
      onDragEnter: this.props.onDragEnter
      // stop propagation if the module is blocked so Layout settings don't open on sidebar when clicking it
      ,
      onMouseUp: e => !isEditable && e.stopPropagation(),
      onMouseLeave: () => this.props.setIsHoveringSubSection(false),
      children: /*#__PURE__*/_jsxs(MouseEventsHandler, {
        type: ELEMENT,
        id: element.get('id'),
        isSelected: isSelected,
        onClick: this.handleClick,
        children: [this.renderAffordances(), this.maybeRenderAlertAndLabels(), this.getEditor(isInlineEditable)]
      })
    }));
    if (draggable) {
      content = connectDragSource(content);
    }
    return this.renderContentWithSnapPlaceholder(content);
  }
}
Element.propTypes = {
  element: PropTypes.object.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  columnIndex: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  isRenderedImage: PropTypes.bool,
  renderedOutput: PropTypes.object,
  mouseIsOver: PropTypes.bool,
  snapPlaceholderPosition: PropTypes.string,
  isVerticalSnapPlaceholder: PropTypes.bool,
  isInlineEditable: PropTypes.bool,
  isEditable: PropTypes.bool,
  isFilePlaceholder: PropTypes.bool,
  somethingSelected: PropTypes.bool,
  isImagePlaceholder: PropTypes.bool,
  isSingleColumnFullWidth: PropTypes.bool,
  retryRenderModule: PropTypes.func.isRequired,
  isResizing: PropTypes.bool,
  canAddNewColumn: PropTypes.bool,
  canResize: PropTypes.bool,
  columnWidth: PropTypes.number,
  columnHeight: PropTypes.number,
  message: PropTypes.string,
  isNarrowColumn: PropTypes.bool.isRequired,
  isMobileBreakpointMode: PropTypes.bool.isRequired,
  isReadOnlyMode: PropTypes.bool,
  // withDropComponent props
  getElementRef: PropTypes.func,
  onDragEnter: PropTypes.func,
  // Injected by React DnD:
  connectDragPreview: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isOver: PropTypes.bool.isRequired,
  // React DnD placeholder layer:
  item: PropTypes.object,
  isHoveringFile: PropTypes.bool,
  initialOffset: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }),
  currentOffset: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }),
  dragElement: PropTypes.func.isRequired,
  dropElementOnSection: PropTypes.func.isRequired,
  dropElementOnElement: PropTypes.func.isRequired,
  dropElementOnPlaceholder: PropTypes.func.isRequired,
  selectElement: PropTypes.func.isRequired,
  numSmartRules: PropTypes.number,
  moduleSpec: PropTypes.object.isRequired,
  shouldShowPlaceholder: PropTypes.bool,
  openModalAction: PropTypes.func,
  setIsHoveringSubSection: PropTypes.func,
  stack: PropTypes.string
};
export default compose(connect(mapStateToProps, mapDispatchToProps), withElementWrapper, withDropComponent, DragSource(ELEMENT, widgetDragSource, monitor => ({
  isDragging: monitor.isDragging()
})), DropTarget([ELEMENT, NativeTypes.FILE], widgetDragTarget, monitor => {
  return {
    isHoveringFile: monitor.getItemType() === NativeTypes.FILE && monitor.isOver(),
    isOver: monitor.isOver()
  };
}), withErrorBoundaryConnect(true, /*#__PURE__*/_jsx(ModuleRenderError, {})))(Element);